import { useId, useState, type FormEvent } from "react";
import { ApiError } from "../api/client";
import type { PmProject, PmProjectFields } from "../api/pm";
import { useCreateProject, useUpdateProject } from "../hooks/usePm";
import { projectCode, projectCodeVariant } from "../lib/projectCode";
import { Sheet } from "./Sheet";

const DEFAULT_CURRENCY = "EUR";
const MAX_CODE_ATTEMPTS = 9;

/** Message affichable d'un refus de PM, selon qu'on crée ou qu'on modifie. */
function projectErrorMessage(e: unknown, editing: boolean): string {
  if (e instanceof ApiError) {
    if (e.status === 409)
      return editing
        ? "Projet terminé ou annulé : il n'est plus modifiable."
        : "Un projet de ce nom existe déjà : précisez le nom.";
    if (e.status === 403)
      return editing
        ? "Modification refusée pour ce projet."
        : "Création de projet refusée. Si vos droits viennent de changer, reconnectez-vous.";
    if (e.status === 404) return "Projet introuvable.";
    if (e.status === 422 || e.status === 400)
      return "Champs refusés par Project Management : vérifiez le nom et les dates.";
  }
  return "Enregistrement impossible pour le moment. Réessayez dans un instant.";
}

function dateField(iso: string | undefined): string {
  return iso ? iso.slice(0, 10) : "";
}

/** Une date vide n'est envoyée que pour effacer une date existante : PM
 * l'efface sur une chaîne vide en modification. */
function dateEntry(
  key: "start_date" | "end_date",
  value: string,
  previous: string | undefined,
): Partial<PmProjectFields> {
  if (value) return { [key]: value };
  return previous ? { [key]: "" } : {};
}

/** Création (sans `project`) ou modification d'une fiche projet PM. */
export function ProjectFormSheet({
  project,
  initialName,
  onClose,
  onSaved,
}: {
  project?: PmProject;
  /** Nom proposé à la création. */
  initialName?: string;
  onClose: () => void;
  onSaved: (p: PmProject) => void;
}) {
  const editing = Boolean(project);
  const [name, setName] = useState(project?.name ?? initialName ?? "");
  const code = project?.code ?? projectCode(name);
  const [description, setDescription] = useState(project?.description ?? "");
  const [startDate, setStartDate] = useState(dateField(project?.start_date));
  const [endDate, setEndDate] = useState(dateField(project?.end_date));
  const [budget, setBudget] = useState(
    project?.budget_estimated !== undefined
      ? String(project.budget_estimated)
      : "",
  );
  const [error, setError] = useState<string | null>(null);
  const formId = useId();
  const nameId = useId();
  const descriptionId = useId();
  const startId = useId();
  const endId = useId();
  const budgetId = useId();

  const currency = project?.budget_currency || DEFAULT_CURRENCY;
  const create = useCreateProject();
  const update = useUpdateProject();
  const pending = create.isPending || update.isPending;
  const datesInverted = !!startDate && !!endDate && endDate < startDate;
  const budgetValue = budget.trim() === "" ? undefined : Number(budget);
  const budgetInvalid =
    budgetValue !== undefined &&
    (!Number.isInteger(budgetValue) || budgetValue < 0);
  const canSubmit =
    !pending && name.trim().length > 0 && !datesInverted && !budgetInvalid;

  /** Le code vient du nom ; s'il est pris, on essaie CODE-2, CODE-3… */
  async function createWithFreeCode(fields: PmProjectFields) {
    for (let attempt = 1; ; attempt++) {
      try {
        return await create.mutateAsync({
          code: projectCodeVariant(code, attempt),
          ...fields,
        });
      } catch (err) {
        const taken = err instanceof ApiError && err.status === 409;
        if (!taken || attempt >= MAX_CODE_ATTEMPTS) throw err;
      }
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const fields: PmProjectFields = {
      name: name.trim(),
      description: description.trim(),
      ...dateEntry("start_date", startDate, project?.start_date),
      ...dateEntry("end_date", endDate, project?.end_date),
      ...(budgetValue !== undefined
        ? {
            budget_estimated: budgetValue,
            budget_currency: currency,
          }
        : {}),
    };
    setError(null);
    try {
      const saved = project
        ? await update.mutateAsync({ id: project.id, patch: fields })
        : await createWithFreeCode(fields);
      onSaved(saved);
    } catch (err) {
      setError(projectErrorMessage(err, editing));
    }
  }

  return (
    <Sheet
      title={editing ? "Modifier le projet" : "Nouveau projet"}
      onClose={onClose}
      footer={
        <div className="sheet-cta">
          <button
            type="submit"
            form={formId}
            className="primary"
            disabled={!canSubmit}
          >
            {pending ? "Enregistrement…" : editing ? "Enregistrer" : "Créer"}
          </button>
        </div>
      }
    >
      <form
        id={formId}
        className="inline-form reimpute-form"
        onSubmit={(e) => void handleSubmit(e)}
      >
        <label htmlFor={nameId}>
          Nom
          <input
            id={nameId}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            required
          />
        </label>
        {name.trim() && (
          <p className="form-hint">
            Code {editing ? "" : "attribué : "}
            {code}
          </p>
        )}
        <label htmlFor={descriptionId}>
          Description (facultatif)
          <textarea
            id={descriptionId}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
            rows={3}
          />
        </label>
        <label htmlFor={startId}>
          Début (facultatif)
          <input
            id={startId}
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </label>
        <label htmlFor={endId}>
          Fin (facultatif)
          <input
            id={endId}
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </label>
        <label htmlFor={budgetId}>
          Budget estimé en {currency} (facultatif)
          <input
            id={budgetId}
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
          />
        </label>
        {datesInverted && (
          <p className="form-hint" role="alert">
            La fin précède le début.
          </p>
        )}
        {budgetInvalid && (
          <p className="form-hint" role="alert">
            Le budget est un montant entier positif.
          </p>
        )}
        {error && (
          <p className="reimpute-error" role="alert">
            {error}
          </p>
        )}
      </form>
    </Sheet>
  );
}

export default ProjectFormSheet;
